import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, ExternalLink, Download, Clock, CheckCircle2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ContractSession {
  id: number;
  contractTitle: string;
  status: string;
  guestName: string | null;
  guestEmail: string | null;
  signedAt: string | null;
  sentAt: string | null;
  expiresAt: string | null;
  token: string;
}

function formatDate(d: string | Date | null) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function ContractsWidget() {
  const { toast } = useToast();
  const { data: contracts, isLoading, isError } = useQuery<ContractSession[]>({
    queryKey: ["/api/my/contracts"],
  });

  const handleDownload = async (sessionId: number, title: string) => {
    try {
      const res = await apiRequest("GET", `/api/contract-sessions/${sessionId}/download`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/[^a-zA-Z0-9_-]/g, "_")}_signed.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Download failed", description: "Could not download the contract PDF.", variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Contracts</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-20 w-full" /></CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="contracts-widget-error">
        <CardContent className="p-4 text-sm text-muted-foreground">
          Unable to load contracts. Please try again later.
        </CardContent>
      </Card>
    );
  }

  if (!contracts || contracts.length === 0) {
    return (
      <Card data-testid="contracts-widget-empty">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Contracts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No contracts assigned to you yet.</p>
        </CardContent>
      </Card>
    );
  }

  const pending = contracts.filter(c => c.status === "pending");
  const signed = contracts.filter(c => c.status === "signed");

  return (
    <Card data-testid="contracts-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4 text-amber-600" />
          Contracts
          {pending.length > 0 && (
            <Badge variant="outline" className="ml-auto bg-amber-50 text-amber-700 border-amber-200" data-testid="badge-pending-contracts">
              {pending.length} pending
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {pending.map(c => (
          <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-amber-200 bg-amber-50/50" data-testid={`contract-pending-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                <span className="font-medium text-sm truncate">{c.contractTitle}</span>
              </div>
              {c.sentAt && <p className="text-xs text-muted-foreground mt-0.5 ml-5">Sent {formatDate(c.sentAt)}</p>}
            </div>
            <Button
              size="sm"
              className="shrink-0 ml-3"
              onClick={() => window.open(`/sign/${c.token}`, "_blank")}
              data-testid={`button-sign-contract-${c.id}`}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Sign Now
            </Button>
          </div>
        ))}
        {signed.map(c => (
          <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`contract-signed-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
                <span className="font-medium text-sm truncate">{c.contractTitle}</span>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px]">Signed</Badge>
              </div>
              {c.signedAt && <p className="text-xs text-muted-foreground mt-0.5 ml-5">Signed {formatDate(c.signedAt)}</p>}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 ml-3"
              onClick={() => handleDownload(c.id, c.contractTitle)}
              data-testid={`button-download-contract-${c.id}`}
            >
              <Download className="h-3.5 w-3.5 mr-1" /> PDF
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
